import React from 'react';
import styles from './index.module.scss';

const SIZE_CONFIG = {
  small: { fontSize: '14px', paddingY: '10px', paddingX: '12px', iconSize: 18, shadowDepth: 5, borderRadius: '12px' },
  medium: { fontSize: '18px', paddingY: '12.5px', paddingX: '20px', iconSize: 22, shadowDepth: 5, borderRadius: '12px' },
  large: { fontSize: '20px', paddingY: '15px', paddingX: '24px', iconSize: 26, shadowDepth: 6, borderRadius: '12px' },
  xl: { fontSize: '24px', paddingY: '13.5px', paddingX: '28px', iconSize: 32, shadowDepth: 8, borderRadius: '16px' },
};

const VARIANT_COLORS = {
  dark: { bg: '#000', text: '#fff' },
  light: { bg: '#fff', text: '#000' },
  secondary: { bg: 'transparent', text: '#fff', border: '1px solid #fff' },
  lightTransparent: { bg: 'rgba(255,255,255,.10)', text: '#fff', shadow: 'rgba(255,255,255,.15)' },
  darkTransparent: { bg: 'rgba(0,0,0,.10)', text: '#000', shadow: 'rgba(0,0,0,.15)' },
};

const IslandButton = ({
  // Content
  text,
  icon,
  iconPosition = 'left',
  iconOnly = false,
  children,

  // Size
  size = 'medium',
  fullWidth = false,

  // Colors
  variant = 'light',
  bgColor,
  color,

  // Shadow (3D effect)
  shadowEnabled = true,
  shadowColor,

  // Background
  backgroundImage,

  // Other
  disabled = false,
  loading = false,
  onClick,
  className = '',
  type = 'button',
}) => {
  const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG.medium;
  const variantConfig = VARIANT_COLORS[variant] || VARIANT_COLORS.light;

  const bg = bgColor || variantConfig.bg;
  const textColor = color || variantConfig.text;

  const buttonClasses = [
    styles.islandButton,
    styles[size],
    styles[variant],
    fullWidth && styles.fullWidth,
    disabled && styles.disabled,
    iconOnly && styles.iconOnly,
    className,
  ].filter(Boolean).join(' ');

  const renderContent = () => {
    if (loading) {
      return <span className={styles.loader}>Loading...</span>;
    }

    const iconElement = icon && (
      <span className={styles.icon} style={{ '--icon-size': `${sizeConfig.iconSize}px` }}>
        {icon}
      </span>
    );

    if (iconOnly && icon) {
      return iconElement;
    }

    return (
      <>
        {iconPosition === 'left' && iconElement}
        {(text || children) && (
          <span className={styles.text}>{text || children}</span>
        )}
        {iconPosition === 'right' && iconElement}
      </>
    );
  };

  // Build style object, only include shadow if explicitly provided or disabled
  const styleVars = {
    '--button-padding-y': sizeConfig.paddingY,
    '--button-padding-x': sizeConfig.paddingX,
    '--button-font-size': sizeConfig.fontSize,
    '--button-bg': bg,
    '--button-color': textColor,
    '--button-shadow-depth': `${sizeConfig.shadowDepth}px`,
    '--button-radius': sizeConfig.borderRadius || '12px',
    '--button-bg-image': backgroundImage || 'none',
  };

  // For iconOnly buttons, calculate explicit square size (content + shadow)
  if (iconOnly) {
    const paddingY = parseFloat(sizeConfig.paddingY);
    const totalSize = (paddingY * 2) + sizeConfig.iconSize + sizeConfig.shadowDepth;
    styleVars['--button-size'] = `${totalSize}px`;
  }

  // Set border if variant has one
  if (variantConfig.border) {
    styleVars['--button-border'] = variantConfig.border;
    styleVars['--button-border-bottom'] = '1px solid #fff';
  }

  // Set shadow color: explicit prop > variant shadow > CSS color-mix
  if (shadowColor && shadowEnabled) {
    styleVars['--button-shadow'] = shadowColor;
  } else if (variantConfig.shadow && shadowEnabled) {
    styleVars['--button-shadow'] = variantConfig.shadow;
  } else if (!shadowEnabled) {
    styleVars['--button-shadow'] = 'transparent';
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={buttonClasses}
      style={styleVars}
    >
      <span className={styles.content}>
        {renderContent()}
      </span>
    </button>
  );
};

export default IslandButton;
